import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import {
  getAssessmentLinks,
  createAssessmentLink,
  deleteAssessmentLink,
  getOrganizations,
  getJobRoles,
} from '../../utils/api';
import {
  Plus,
  Trash2,
  Link as LinkIcon,
  X,
  Copy,
  Check,
  ExternalLink,
} from 'lucide-react';

const AssessmentLinks = () => {
  const [links, setLinks] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [formData, setFormData] = useState({
    organization_id: '',
    job_role_id: '',
    link_type: 'job_application',
    title: '',
    description: '',
    expires_at: '',
    max_responses: '',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [linksRes, orgsRes] = await Promise.all([
        getAssessmentLinks(),
        getOrganizations(),
      ]);
      setLinks(linksRes.data);
      setOrganizations(orgsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOrgChange = async (orgId) => {
    setFormData({ ...formData, organization_id: orgId, job_role_id: '' });
    if (orgId) {
      try {
        const response = await getJobRoles(orgId);
        setJobRoles(response.data);
      } catch (error) {
        console.error('Error loading job roles:', error);
      }
    } else {
      setJobRoles([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const data = {
        ...formData,
        organization_id: formData.organization_id || null,
        job_role_id: formData.job_role_id || null,
        expires_at: formData.expires_at || null,
        max_responses: formData.max_responses ? parseInt(formData.max_responses) : null,
      };
      await createAssessmentLink(data);
      setShowModal(false);
      setFormData({
        organization_id: '',
        job_role_id: '',
        link_type: 'job_application',
        title: '',
        description: '',
        expires_at: '',
        max_responses: '',
      });
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create link');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this link?')) {
      return;
    }

    try {
      await deleteAssessmentLink(id);
      loadData();
    } catch (error) {
      console.error('Error deleting link:', error);
    }
  };

  const getAssessmentUrl = (uuid) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/assess/${uuid}`;
  };

  const copyToClipboard = async (uuid) => {
    const url = getAssessmentUrl(uuid);
    await navigator.clipboard.writeText(url);
    setCopiedId(uuid);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tsvmap-orange" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Assessment Links</h1>
          <p className="text-gray-600">Create and manage shareable assessment links</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center px-4 py-2 bg-tsvmap-orange text-white rounded-lg hover:bg-tsvmap-orange-dark transition-colors"
        >
          <Plus className="h-5 w-5 mr-2" />
          Create Link
        </button>
      </div>

      {/* Links List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {links.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {links.map((link) => (
              <div key={link.id} className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">
                        {link.title || 'Untitled Assessment'}
                      </h3>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          link.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {link.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mb-2">
                      {link.organization_name || 'No organization'}
                      {link.job_role_title && ` • ${link.job_role_title}`}
                    </p>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                      <span>Type: {link.link_type.replace('_', ' ')}</span>
                      <span>Responses: {link.response_count || 0}</span>
                      {link.max_responses && (
                        <span>Max: {link.max_responses}</span>
                      )}
                      {link.expires_at && (
                        <span>
                          Expires: {new Date(link.expires_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center bg-gray-100 rounded-lg overflow-hidden">
                      <input
                        type="text"
                        readOnly
                        value={getAssessmentUrl(link.uuid)}
                        className="px-3 py-2 bg-transparent text-sm text-gray-600 w-64 truncate"
                      />
                      <button
                        onClick={() => copyToClipboard(link.uuid)}
                        className="px-3 py-2 hover:bg-gray-200 transition-colors"
                        title="Copy link"
                      >
                        {copiedId === link.uuid ? (
                          <Check className="h-5 w-5 text-green-600" />
                        ) : (
                          <Copy className="h-5 w-5 text-gray-500" />
                        )}
                      </button>
                    </div>
                    <a
                      href={getAssessmentUrl(link.uuid)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-gray-500 hover:text-tsvmap-blue transition-colors"
                      title="Open link"
                    >
                      <ExternalLink className="h-5 w-5" />
                    </a>
                    <button
                      onClick={() => handleDelete(link.id)}
                      className="p-2 text-red-500 hover:text-red-700 transition-colors"
                      title="Delete link"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            <LinkIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No assessment links yet</p>
            <p className="text-sm mt-1">Create your first link to start collecting assessments</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowModal(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Create Assessment Link</h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {error}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Sales Manager Application"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tsvmap-orange focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Link Type
                  </label>
                  <select
                    value={formData.link_type}
                    onChange={(e) => setFormData({ ...formData, link_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tsvmap-orange focus:border-transparent"
                  >
                    <option value="job_application">Job Application</option>
                    <option value="team_assessment">Team Assessment</option>
                    <option value="customer_assessment">Customer Assessment</option>
                    <option value="general">General Assessment</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization (optional)
                  </label>
                  <select
                    value={formData.organization_id}
                    onChange={(e) => handleOrgChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tsvmap-orange focus:border-transparent"
                  >
                    <option value="">No organization</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>

                {jobRoles.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Job Role (optional)
                    </label>
                    <select
                      value={formData.job_role_id}
                      onChange={(e) => setFormData({ ...formData, job_role_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tsvmap-orange focus:border-transparent"
                    >
                      <option value="">No specific role</option>
                      {jobRoles.map((role) => (
                        <option key={role.id} value={role.id}>{role.title}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (optional)
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tsvmap-orange focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expiration Date
                    </label>
                    <input
                      type="date"
                      value={formData.expires_at}
                      onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tsvmap-orange focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Responses
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.max_responses}
                      onChange={(e) => setFormData({ ...formData, max_responses: e.target.value })}
                      placeholder="Unlimited"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tsvmap-orange focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-tsvmap-orange text-white rounded-lg hover:bg-tsvmap-orange-dark"
                  >
                    Create Link
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AssessmentLinks;
